'use client';

var __jsx = React.createElement;
import React, { useEffect, useMemo, useRef, useState } from 'react';
import AuthFormTemplate from '../../../../templates/auth-form.template';
import { MuscleButton } from '@modules/shared/components/buttons';
import { useTranslations } from 'next-intl';
import { useCustomer } from '@lib/context/shared/customer-context';
import EmailInput from '@modules/shared/components/email-input';
import PasswordInput from '@modules/shared/components/password-input';
import TextWithLinks from '@modules/shared/components/text-with-links';
import useErrorsManagement from '@lib/hooks/use-errors-management';
import ERROR_CODES from '@lib/error-codes';
import useMuscleRouter from '@lib/hooks/use-muscle-router';
import SocialMediaAuth from '../../../social-media-auth';
import { Divider } from '@mui/material';
import APP_CONFIGURATION from '@lib/app.config';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINTS } from '@lib/constants';
import useViewportDimensions from '@lib/hooks/use-viewport-dimensions';
import { permissionsManager } from '@lib/permissions/permissions-manager';
const CustomerLoginForm = _ref => {
  let {
    onLoginCallback,
    onClickRegisterHere,
    onClickForgotPassword
  } = _ref;
  const allowSocialMediaAuth = (process.env.NEXT_PUBLIC_SOCIAL_MEDIA_AUTH || 'false') === 'true';
  const t = useTranslations('Shared');
  const {
    login
  } = useCustomer();
  const {
    0: emailEntry,
    1: setEmailEntry
  } = useState('');
  const {
    0: passwordEntry,
    1: setPasswordEntry
  } = useState('');
  const {
    0: loggingIn,
    1: setLoggingIn
  } = useState(false);
  const {
    0: credentialsIncorrect,
    1: setCredentialsIncorrect
  } = useState(false);
  const {
    0: emailIsValid,
    1: setEmailIsValid
  } = useState(true);
  const {
    manageBackendTranslatedError
  } = useErrorsManagement();
  const router = useMuscleRouter();
  const entriesValid = useMemo(() => {
    return emailIsValid && emailEntry !== '' && passwordEntry !== '';
  }, [passwordEntry, emailEntry, emailIsValid]);
  const buttonRef = useRef(null);
  const isMobile = useMediaQuery({
    query: "(max-width: ".concat(BREAKPOINTS.md, "px)")
  });
  const {
    height,
    width
  } = useViewportDimensions(0.8, isMobile ? 0.7 : 0.8);
  const showRegisterHere = permissionsManager.showRegisterButton();
  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      onClickLogin();
    }
  };
  useEffect(() => {
    window.removeEventListener('keydown', handleKeyPress);
    if (entriesValid) {
      // Add event listener for keypress
      window.addEventListener('keydown', handleKeyPress);
    }
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [entriesValid, emailEntry, passwordEntry]);
  const validateLoginResult = async loggingResult => {
    setLoggingIn(false);
    if (loggingResult.result.error && loggingResult.result.code === ERROR_CODES.Customers.CUSTOMERS_AUTH_SERVICES_ERROR) {
      showAuthServicesError(loggingResult.result);
      return;
    }
    if (loggingResult.status) {
      onLoginCallback && onLoginCallback();
    } else {
      setCredentialsIncorrect(true);
    }
  };
  const onProviderCallBack = async (token, provider) => {
    setLoggingIn(true);
    try {
      const loginResult = await login({}, {
        provider_token: token,
        provider_id: provider
      });
      await validateLoginResult(loginResult);
    } catch (err) {} finally {
      setLoggingIn(false);
    }
  };
  const onClickLogin = async () => {
    if (entriesValid) {
      setLoggingIn(true);
      let loginCustomer = {
        email: emailEntry,
        password: passwordEntry
      };
      const loggingResult = await login(loginCustomer);
      await validateLoginResult(loggingResult);
      setLoggingIn(false);
    }
  };
  const showAuthServicesError = async data => {
    const actions = [{
      buttonVariant: isMobile ? "solidXL" : "solidMd",
      text: t("Errors.RETURN_TO_HOME"),
      onClick: () => {
        router.push("/");
      }
    }];
    data.error = t("Errors.AUTH_SERVICE_ERROR_TITLE");
    data.message = t("Errors.AUTH_SERVICE_ERROR_MESSAGE");
    manageBackendTranslatedError(data, actions);
  };
  return __jsx(AuthFormTemplate, null, __jsx("div", {
    className: "flex flex-col gap-4",
    style: {
      width: isMobile ? width : '384px'
    }
  }, __jsx("p", {
    className: "text-center tx-pms-regular"
  }, t('Auth.LOGIN_SUBTITLE')), allowSocialMediaAuth && __jsx(React.Fragment, null, __jsx(SocialMediaAuth, {
    type: "login",
    loading: loggingIn,
    className: "gap-4",
    successCallback: onProviderCallBack,
    setLoading: setLoggingIn
  }), __jsx(Divider, null, __jsx("span", {
    className: "text-neutral-400"
  }, t('OR')))), __jsx(EmailInput, {
    dataTestId: "login-email-input",
    value: emailEntry,
    valueIsInvalid: credentialsIncorrect || !emailIsValid,
    onChangeValue: value => setEmailEntry(value),
    setValueIsInvalid: isInvalid => setEmailIsValid(!isInvalid),
    fullLength: true,
    required: true,
    labelClassName: "tx-psm-bold"
  }), __jsx(PasswordInput, {
    dataTestId: "login-password-input",
    value: passwordEntry,
    valueIsInvalid: credentialsIncorrect,
    onChangeValue: value => setPasswordEntry(value),
    fullLength: true,
    required: true,
    labelClassName: "tx-psm-bold"
  }), credentialsIncorrect ? __jsx("div", {
    className: "text-error-main tx-psm-regular"
  }, t('Auth.VERIFY_EMAIL_AND_PASSWORD')) : null), !APP_CONFIGURATION.config.demo && showRegisterHere && __jsx("div", {
    className: "flex flex-col gap-8 items-center text-center"
  }, __jsx(MuscleButton, {
    dataTestId: "login-forgot-password-button",
    variant: "link",
    onClick: onClickForgotPassword
  }, t('Auth.FORGOT_MY_PASSWORD')), __jsx(TextWithLinks, {
    text: t('Auth.DO_NOT_HAVE_ACCOUNT_QUESTION'),
    textStyle: "tx-psm-regular",
    linksStyle: "text-primary_text tx-psm-bold",
    linksActions: [() => onClickRegisterHere ? onClickRegisterHere() : null]
  })), __jsx("div", {
    ref: buttonRef
  }, __jsx(MuscleButton, {
    dataTestId: "login-login-button",
    isLoading: loggingIn,
    variant: "solidMd",
    onClick: onClickLogin,
    disabled: !entriesValid
  }, t('Auth.LOGIN'))));
};
export default CustomerLoginForm;